import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../../Component/Layout";
import { PURCHASE_API } from "../../apiConfig";
import { useLocation, useNavigate } from "react-router-dom";

/* ==========================================================================
   Payment Run — Account Receivable
   Filters: Invoice (Billing ID) / Purchase Order / Customer

   GET  ${PURCHASE_API}/payment-run/filters
       -> { billingIds: [...], purchaseOrders: [...], customers: [{partner_code, business_partner_name}] }

   GET  ${PURCHASE_API}/payment-run?billingId=&purchaseOrder=&customer=
       -> { rows: [{ billingId, soId, date, customerName, purchaseOrder,
                      invoiceAmount, advanceAmount, receivedAmount, amount, currency }] }

   GET  ${PURCHASE_API}/payment-run/:billingId/detail
       -> { billingId, soId, date, customerName, purchaseOrder, currency,
            invoiceAmount, totalAdvance, totalReceived, balanceDue,
            advancePayments: [...], receiptHistory: [...] }

   POST ${PURCHASE_API}/payment-run/receive
       body: { billingId, soId, purchaseOrder, customerName, invoiceAmount,
               advanceAmount, receivedAmount, referenceNumber, currency,
               receivedDate, remarks }
   ========================================================================== */

const fmtAmount = (val) =>
    val === null || val === undefined || val === ""
        ? "—"
        : Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (isoStr) => {
    if (!isoStr) return "—";
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: "13px",
        borderColor: "#dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#b3b3b3" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
        color: state.isSelected ? "white" : "#334155",
        cursor: "pointer",
        fontSize: "13px",
    }),
};

const filterCardStyle = { background: "#ffffff", borderRadius: 8, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 18 };
const filterLabelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#334155" };

const thStyle = {
    padding: "12px 14px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderBottom: "2px solid #e2e8f0",
    background: "#f1f5f9",
    position: "sticky",
    top: 0,
};

const tdStyle = {
    padding: "11px 14px",
    fontSize: "13px",
    color: "#334155",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #f1f5f9",
};

const receiveBtnStyle = {
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#ffffff",
    background: "#2563eb",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    letterSpacing: "0.02em",
};

const receiveBtnDisabledStyle = {
    ...receiveBtnStyle,
    background: "#cbd5e1",
    cursor: "not-allowed",
};

const tabGroupStyle = {
    display: "inline-flex",
    background: "#f1f5f9",
    borderRadius: "10px",
    padding: "4px",
    gap: "4px",
};

const tabBtnStyle = {
    padding: "8px 20px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#64748b",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
};

const tabBtnActiveStyle = {
    ...tabBtnStyle,
    color: "#0f172a",
    background: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
};

const badgeStyle = {
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 9px",
    borderRadius: "999px",
    display: "inline-block",
};

function PaymentRun() {
    const location = useLocation();
    const navigate = useNavigate();
    const [filterOptions, setFilterOptions] = useState({
        billingIds: [],
        purchaseOrders: [],
        customers: [],
    });

    const [filters, setFilters] = useState({
        billingId: "",
        purchaseOrder: "",
        customer: "",
    });

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Active / Posted toggle
    // Active -> invoices where a payment is still due (amount > 0)
    // Posted -> invoices fully received (amount <= 0 / "Settled")
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("payment_run_active_tab") || "active");
    useEffect(() => {
        sessionStorage.setItem("payment_run_active_tab", activeTab);
    }, [activeTab]);

    // Receive panel state
    const [showReceiveModal, setShowReceiveModal] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [activeBillingId, setActiveBillingId] = useState(null);
    const [receiveAmount, setReceiveAmount] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [receivedDate, setReceivedDate] = useState(todayISO());
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Load dropdown options once on mount
    useEffect(() => {
        axios
            .get(`${PURCHASE_API}/payment-run/filters`)
            .then((res) => setFilterOptions(res.data || {}))
            .catch((err) => {
                console.error("PAYMENT RUN FILTERS ERROR:", err);
            });
    }, []);

    const billingIdOptions = useMemo(
        () => (filterOptions.billingIds || []).map((id) => ({ value: id, label: id })),
        [filterOptions.billingIds]
    );

    const purchaseOrderOptions = useMemo(
        () => (filterOptions.purchaseOrders || []).map((po) => ({ value: po, label: po })),
        [filterOptions.purchaseOrders]
    );

    const customerOptions = useMemo(
        () =>
            (filterOptions.customers || []).map((c) => ({
                value: c.business_partner_name,
                label: c.business_partner_name,
            })),
        [filterOptions.customers]
    );

    const fetchRows = (activeFilters) => {
        setLoading(true);
        setError("");

        const params = {};
        if (activeFilters.billingId) params.billingId = activeFilters.billingId;
        if (activeFilters.purchaseOrder) params.purchaseOrder = activeFilters.purchaseOrder;
        if (activeFilters.customer) params.customer = activeFilters.customer;

        axios
            .get(`${PURCHASE_API}/payment-run`, { params })
            .then((res) => {
                setRows(res.data?.rows || []);
            })
            .catch((err) => {
                console.error("PAYMENT RUN FETCH ERROR:", err);
                setError("Failed to load records. Please try again.");
                setRows([]);
            })
            .finally(() => setLoading(false));
    };

    // Auto-fetch whenever any filter changes (debounced), including initial load
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchRows(filters);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const displayCurrency = useMemo(() => {
        const withCurrency = rows.find((r) => r.currency);
        return withCurrency ? withCurrency.currency : "INR";
    }, [rows]);

    // An invoice belongs in Posted only once it's fully received (amount <= 0).
    // Everything still awaiting payment (in full or in part) stays in Active.
    const isFullyReceived = (row) => Number(row.amount) <= 0;
    const activeData = useMemo(() => rows.filter((r) => !isFullyReceived(r)), [rows]);
    const postedData = useMemo(() => rows.filter((r) => isFullyReceived(r)), [rows]);
    const activeCount = activeData.length;
    const postedCount = postedData.length;
    const tabData = activeTab === "active" ? activeData : postedData;

    // ---- Receive panel handlers ----

    // ---- Navigate to Billing screen and open this invoice's modal there ----
    const openBillingDetail = (billingId) => {
        navigate("/cable/billing/billing", {
            state: {
                openBiId: billingId,
                returnTo: location.pathname
            }
        });
    };

    const openReceiveModal = (billingId) => {
        setActiveBillingId(billingId);
        setShowReceiveModal(true);
        setDetail(null);
        setReceiveAmount("");
        setReferenceNumber("");
        setReceivedDate(todayISO());
        setRemarks("");
        setDetailLoading(true);

        axios
            .get(`${PURCHASE_API}/payment-run/${encodeURIComponent(billingId)}/detail`)
            .then((res) => {
                setDetail(res.data || null);
            })
            .catch((err) => {
                console.error("PAYMENT RUN DETAIL ERROR:", err);
                toast.error("Failed to load invoice details.");
                setShowReceiveModal(false);
            })
            .finally(() => setDetailLoading(false));
    };

    const closeReceiveModal = () => {
        if (submitting) return;
        setShowReceiveModal(false);
        setDetail(null);
        setActiveBillingId(null);
    };

    const handleSubmitReceipt = () => {
        if (!detail) return;

        const amountNum = Number(receiveAmount);
        if (!receiveAmount || isNaN(amountNum) || amountNum <= 0) {
            toast.error("Enter a valid amount received.");
            return;
        }
        if (amountNum > detail.balanceDue) {
            toast.error(`Amount cannot exceed the balance due (${fmtAmount(detail.balanceDue)}).`);
            return;
        }
        if (!referenceNumber.trim()) {
            toast.error("Reference number is required.");
            return;
        }
        if (!receivedDate) {
            toast.error("Select the date received.");
            return;
        }

        setSubmitting(true);

        axios
            .post(`${PURCHASE_API}/payment-run/receive`, {
                billingId: detail.billingId,
                soId: detail.soId,
                purchaseOrder: detail.purchaseOrder,
                customerName: detail.customerName,
                invoiceAmount: detail.invoiceAmount,
                advanceAmount: detail.totalAdvance,
                receivedAmount: amountNum,
                referenceNumber: referenceNumber.trim(),
                currency: detail.currency,
                receivedDate,
                remarks: remarks.trim() || null,
            })
            .then(() => {
                toast.success("Payment receipt recorded.");
                setShowReceiveModal(false);
                setDetail(null);
                setActiveBillingId(null);
                fetchRows(filters);
            })
            .catch((err) => {
                console.error("PAYMENT RUN RECEIVE ERROR:", err);
                toast.error(err.response?.data?.message || "Failed to record receipt. Please try again.");
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Layout>
            <div style={{ padding: "30px 40px", backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>

                {/* Page Header */}
                <div style={{ marginBottom: "28px" }}>
                    <h2 style={{ margin: "0 0 4px 0", color: "#0f172a", fontWeight: "700", fontSize: "24px" }}>
                        Payment Run
                    </h2>
                    <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                        Billing invoices by Invoice, Purchase Order and Customer
                    </p>
                </div>

                {/* Filter Card */}
                <div style={filterCardStyle}>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "nowrap" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={filterLabelStyle}>Invoice</label>
                            <Select
                                options={billingIdOptions}
                                value={billingIdOptions.find((o) => o.value === filters.billingId) || null}
                                onChange={(opt) => handleChange("billingId", opt ? opt.value : "")}
                                placeholder="Select"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={dropdownOptionStyle}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={filterLabelStyle}>Purchase Order</label>
                            <Select
                                options={purchaseOrderOptions}
                                value={purchaseOrderOptions.find((o) => o.value === filters.purchaseOrder) || null}
                                onChange={(opt) => handleChange("purchaseOrder", opt ? opt.value : "")}
                                placeholder="Select"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={dropdownOptionStyle}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <label style={filterLabelStyle}>Customer</label>
                            <Select
                                options={customerOptions}
                                value={customerOptions.find((o) => o.value === filters.customer) || null}
                                onChange={(opt) => handleChange("customer", opt ? opt.value : "")}
                                placeholder="Select"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={dropdownOptionStyle}
                            />
                        </div>
                    </div>
                </div>

                {error && (
                    <div style={{
                        background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
                        borderRadius: "10px", padding: "12px 16px", marginBottom: "18px", fontSize: "13px"
                    }}>
                        {error}
                    </div>
                )}

                {/* Active / Posted Toggle */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
                    <div style={tabGroupStyle}>
                        <button
                            onClick={() => setActiveTab("active")}
                            style={activeTab === "active" ? tabBtnActiveStyle : tabBtnStyle}
                        >
                            Active
                        </button>
                        <button
                            onClick={() => setActiveTab("posted")}
                            style={activeTab === "posted" ? tabBtnActiveStyle : tabBtnStyle}
                        >
                            Posted
                            {postedCount > 0 && (
                                <span style={{ ...badgeStyle, background: "#dcfce7", color: "#15803d" }}>
                                    {postedCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div style={{
                    background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", overflow: "hidden"
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px",
                        padding: "14px 18px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc"
                    }}>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Currency
                        </span>
                        <span style={{
                            fontSize: "12px", fontWeight: "700", color: "#334155",
                            background: "#e2e8f0", borderRadius: "6px", padding: "3px 10px"
                        }}>
                            {displayCurrency}
                        </span>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "820px" }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Invoice</th>
                                    <th style={thStyle}>Customer</th>
                                    <th style={thStyle}>Purchase Order</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Balance Due</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                                            Loading records...
                                        </td>
                                    </tr>
                                ) : tabData.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                                            {activeTab === "posted"
                                                ? "No fully settled invoices yet."
                                                : "No records found for the selected filters."}
                                        </td>
                                    </tr>
                                ) : (
                                    tabData.map((row) => {
                                        const fullyReceived = Number(row.amount) <= 0;
                                        return (
                                            <tr key={row.billingId}>
                                                <td style={{ ...tdStyle, fontWeight: "600" }}>
                                                    <span
                                                        onClick={() => openBillingDetail(row.billingId)}
                                                        style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                                    >
                                                        {row.billingId}
                                                    </span>
                                                </td>
                                                <td style={tdStyle}>{row.customerName || ""}</td>
                                                <td style={tdStyle}>{row.purchaseOrder || ""}</td>
                                                <td style={{ ...tdStyle, textAlign: "right", fontWeight: "600" }}>
                                                    {fmtAmount(row.amount)}
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: "center" }}>
                                                    <button
                                                        style={fullyReceived ? receiveBtnDisabledStyle : receiveBtnStyle}
                                                        disabled={fullyReceived}
                                                        onClick={() => openReceiveModal(row.billingId)}
                                                    >
                                                        Receive
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Receive Modal */}
                {showReceiveModal && (
                    <div
                        style={{
                            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            zIndex: 1050, padding: "20px"
                        }}
                        onClick={closeReceiveModal}
                    >
                        <div
                            style={{
                                background: "#fff", borderRadius: "14px", width: "100%", maxWidth: "620px",
                                maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden",
                                boxShadow: "0 20px 50px rgba(0,0,0,0.25)"
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div style={{
                                padding: "18px 24px", borderBottom: "1px solid #e2e8f0",
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                background: "#f8fafc"
                            }}>
                                <div>
                                    <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                                        {activeBillingId}
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                                        Record an amount received against this invoice
                                    </div>
                                </div>
                                <button
                                    onClick={closeReceiveModal}
                                    style={{ background: "transparent", border: "none", fontSize: "18px", cursor: "pointer", color: "#94a3b8" }}
                                >
                                    ✖
                                </button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: "22px 24px", overflowY: "auto", flex: 1 }}>
                                {detailLoading || !detail ? (
                                    <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontSize: "13px" }}>
                                        Loading invoice details...
                                    </div>
                                ) : (
                                    <>
                                        {/* Invoice summary */}
                                        <div style={{
                                            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px",
                                            padding: "16px 18px", marginBottom: "18px",
                                            display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: "10px", columnGap: "16px"
                                        }}>
                                            <SummaryField label="Customer" value={detail.customerName || "—"} />
                                            <SummaryField label="Date" value={fmtDate(detail.date)} />
                                            <SummaryField label="Purchase Order" value={detail.purchaseOrder || "—"} />
                                            <SummaryField label="Currency" value={detail.currency} />
                                            <SummaryField label="Invoice Amount" value={fmtAmount(detail.invoiceAmount)} />
                                            <SummaryField label="Advance Adjusted" value={fmtAmount(detail.totalAdvance)} />
                                            <SummaryField label="Received So Far" value={fmtAmount(detail.totalReceived)} />
                                            <SummaryField
                                                label="Balance Due"
                                                value={fmtAmount(detail.balanceDue)}
                                                highlight
                                            />
                                        </div>

                                        {/* Advance payment history */}
                                        <div style={{ marginBottom: "18px" }}>
                                            <div style={{ fontSize: "13px", fontWeight: "700", color: "#334155", marginBottom: "8px" }}>
                                                Advance Payments
                                            </div>
                                            {detail.advancePayments && detail.advancePayments.length > 0 ? (
                                                <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={miniThStyle}>Date</th>
                                                                <th style={miniThStyle}>Reference</th>
                                                                <th style={{ ...miniThStyle, textAlign: "right" }}>Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {detail.advancePayments.map((ap) => (
                                                                <tr key={ap.id}>
                                                                    <td style={miniTdStyle}>{fmtDate(ap.payment_date)}</td>
                                                                    <td style={miniTdStyle}>{ap.reference_number || "—"}</td>
                                                                    <td style={{ ...miniTdStyle, textAlign: "right" }}>{fmtAmount(ap.advance)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div style={{ fontSize: "12px", color: "#94a3b8", padding: "10px 2px" }}>
                                                    No advance payments recorded for this sales order.
                                                </div>
                                            )}
                                        </div>

                                        {/* Prior receipts */}
                                        {detail.receiptHistory && detail.receiptHistory.length > 0 && (
                                            <div style={{ marginBottom: "18px" }}>
                                                <div style={{ fontSize: "13px", fontWeight: "700", color: "#334155", marginBottom: "8px" }}>
                                                    Receipts Already Recorded
                                                </div>
                                                <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={miniThStyle}>Date</th>
                                                                <th style={miniThStyle}>Reference</th>
                                                                <th style={{ ...miniThStyle, textAlign: "right" }}>Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {detail.receiptHistory.map((r) => (
                                                                <tr key={r.id}>
                                                                    <td style={miniTdStyle}>{fmtDate(r.received_date)}</td>
                                                                    <td style={miniTdStyle}>{r.reference_number}</td>
                                                                    <td style={{ ...miniTdStyle, textAlign: "right" }}>{fmtAmount(r.received_amount)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Entry form */}
                                        <div style={{ fontSize: "13px", fontWeight: "700", color: "#334155", marginBottom: "10px" }}>
                                            Record New Receipt
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                                            <div>
                                                <label style={filterLabelStyle}>Amount Received ({detail.currency})</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={receiveAmount}
                                                    onChange={(e) => setReceiveAmount(e.target.value)}
                                                    placeholder="0.00"
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={filterLabelStyle}>Reference Number</label>
                                                <input
                                                    type="text"
                                                    value={referenceNumber}
                                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                                    placeholder="UTR / Cheque / Txn No."
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={filterLabelStyle}>Date Received</label>
                                                <input
                                                    type="date"
                                                    value={receivedDate}
                                                    onChange={(e) => setReceivedDate(e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={filterLabelStyle}>Remarks (optional)</label>
                                                <input
                                                    type="text"
                                                    value={remarks}
                                                    onChange={(e) => setRemarks(e.target.value)}
                                                    placeholder="Any note"
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            <div style={{
                                padding: "16px 24px", borderTop: "1px solid #e2e8f0",
                                display: "flex", justifyContent: "flex-end", gap: "10px", background: "#f8fafc"
                            }}>
                                <button
                                    onClick={closeReceiveModal}
                                    disabled={submitting}
                                    style={{
                                        padding: "10px 18px", borderRadius: "8px", border: "1px solid #cbd5e1",
                                        background: "#fff", color: "#334155", fontWeight: "600", fontSize: "13px",
                                        cursor: submitting ? "not-allowed" : "pointer"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitReceipt}
                                    disabled={submitting || detailLoading || !detail}
                                    style={{
                                        padding: "10px 20px", borderRadius: "8px", border: "none",
                                        background: submitting ? "#93c5fd" : "#2563eb", color: "#fff",
                                        fontWeight: "700", fontSize: "13px",
                                        cursor: submitting ? "not-allowed" : "pointer"
                                    }}
                                >
                                    {submitting ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </div>
        </Layout>
    );
}

// ---- small presentational helpers ----

const miniThStyle = {
    padding: "8px 12px", fontSize: "11px", fontWeight: "700", color: "#64748b",
    textAlign: "left", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
    textTransform: "uppercase", letterSpacing: "0.03em"
};

const miniTdStyle = {
    padding: "8px 12px", fontSize: "12px", color: "#334155", borderBottom: "1px solid #f1f5f9"
};

const inputStyle = {
    width: "100%", height: "36px", fontSize: "13px", padding: "6px 10px",
    border: "1px solid #dee2e6", borderRadius: "6px", boxSizing: "border-box"
};

function SummaryField({ label, value, highlight }) {
    return (
        <div>
            <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                {label}
            </div>
            <div style={{ fontSize: "14px", fontWeight: highlight ? "800" : "600", color: highlight ? "#b91c1c" : "#0f172a", marginTop: "2px" }}>
                {value}
            </div>
        </div>
    );
}

export default PaymentRun;